// The sightmap document shape (https://sightmap.org) — views, components and requests describing an
// app for coding agents, authored by BROWSING the live site with no source access.
//
// Zod rather than plain types because the document is produced by a model: this is the untrusted
// boundary `skills/coding-workflow.md` asks to validate at, and a repair loop needs a parse that can fail cleanly.
//
// Two spec fields are deliberately NOT modelled: `source` and `dependencies`. Both are relative paths
// into the app's source tree, and a run that browses a deployed URL has none. Emitting a guess would
// produce a document that looks source-linked and is not.
import { z } from 'zod'

// One request/response field. `type` is prose ('string', 'ISO date', 'array of ids') — the spec does
// not fix a vocabulary and a browsed body cannot prove one.
export const SIGHTMAP_FIELD = z.object({
  name: z.string().min(1),
  type: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
})

/** Structural variants describe element topology, never record values or presentation attributes. */
export interface SightmapStructure {
  tag: string
  type?: string
  role?: string
  children?: SightmapStructure[]
}
export const SIGHTMAP_STRUCTURE: z.ZodType<SightmapStructure> = z.lazy(() => z.object({
  tag: z.string().regex(/^[a-z][a-z0-9-]*$/), type: z.string().optional(), role: z.string().optional(),
  children: z.array(SIGHTMAP_STRUCTURE).optional(),
}).strict())

// Components nest, so the schema is recursive and the interface has to be written out for the type
// annotation z.lazy() needs.
export interface SightmapComponent {
  id?: string
  name: string
  nameSource?: 'classified'
  selector: string | string[]
  structure?: SightmapStructure
  aliases?: Array<{ id: string; selector: string | string[]; path?: string[] }>
  kind?: 'component' | 'control' | 'dialog' | 'region'
  actions?: string[]
  memory?: string[]
  params?: string[]
  properties?: Array<{ name: string; extract: string; description?: string }>
  children?: SightmapComponent[]
}

// `params` names the component properties which identify a concrete record. Extraction uses the
// standard Sightmap property grammar, so those values also reach Subtext and exported sightmaps.
// A value an agent can pull out of a component — the spec's `properties`. The Atlas uses these for the
// text a region carries that is not part of its identity (a store name, a price), which is how a map
// says "read this here" rather than only "this exists".
export const SIGHTMAP_PROPERTY = z.object({
  name: z.string().min(1),
  extract: z.string().min(1),
  description: z.string().min(1).optional(),
})

export const SIGHTMAP_PARAMS = z.array(z.string().regex(/^[a-z][a-z0-9_]*$/)).min(1)

export const SIGHTMAP_COMPONENT: z.ZodType<SightmapComponent> = z.lazy(() =>
  z.object({
    name: z.string().min(1),
    nameSource: z.literal('classified').optional(),
    id: z.string().min(1).optional(),
    // A list of alternatives is allowed by the spec — an app that renders the same region two ways.
    selector: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
    structure: SIGHTMAP_STRUCTURE.optional(),
    aliases: z.array(z.object({ id: z.string().min(1), selector: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]), path: z.array(z.string().min(1)).min(1).optional() }).strict()).optional(),
    kind: z.enum(['component', 'control', 'dialog', 'region']).optional(),
    actions: z.array(z.string().min(1)).optional(),
    memory: z.array(z.string().min(1)).optional(),
    params: SIGHTMAP_PARAMS.optional(),
    properties: z.array(SIGHTMAP_PROPERTY).optional(),
    children: z.array(SIGHTMAP_COMPONENT).optional(),
  }),
)

export const SIGHTMAP_REQUEST = z.object({
  name: z.string().min(1),
  route: z.string().min(1),
  method: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  request: z.object({ fields: z.array(SIGHTMAP_FIELD) }).optional(),
  response: z.object({ fields: z.array(SIGHTMAP_FIELD) }).optional(),
  headers: z.array(z.string().min(1)).optional(),
})
export type SightmapRequest = z.infer<typeof SIGHTMAP_REQUEST>

export const SIGHTMAP_VIEW = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1),
  // View-level memory: what an agent must know about the SCREEN before any component on it makes sense
  // — that a module carries two test ids and only the inner one is stable, that which modules render is
  // merchandising, that the content is geolocated. The Atlas's IKEA map opens with four such notes and
  // they are the most useful thing in it; a document that can only annotate components cannot hold them.
  memory: z.array(z.string().min(1)).optional(),
  // Express-style `:param` segments, `**` for any depth — the Atlas convention (`/:owner/:repo/pulls`,
  // `/**` for a catch-all NotFound).
  route: z.string().min(1),
  description: z.string().min(1).optional(),
  components: z.array(SIGHTMAP_COMPONENT).optional(),
  requests: z.array(SIGHTMAP_REQUEST).optional(),
})
export type SightmapView = z.infer<typeof SIGHTMAP_VIEW>

// The whole document, before it is split into files. Top-level `components`/`requests` are the app-wide
// ones (the Atlas puts these in `shared.yaml`); everything else is scoped to the view it belongs to.
/** The spec version every emitted file declares. */
export const SIGHTMAP_VERSION = 1

export const SIGHTMAP_DOC = z.object({
  version: z.number().int().positive().optional(),
  views: z.array(SIGHTMAP_VIEW),
  components: z.array(SIGHTMAP_COMPONENT).optional(),
  requests: z.array(SIGHTMAP_REQUEST).optional(),
})
export type SightmapDoc = z.infer<typeof SIGHTMAP_DOC>

/** Selectors a component carries, as a list whatever form it was written in. */
export function selectorsOf(component: SightmapComponent): string[] {
  return Array.isArray(component.selector) ? component.selector : [component.selector]
}

/** Every component in the tree, depth-first — the shape most callers actually want. */
export function flattenComponents(components: SightmapComponent[] | undefined): SightmapComponent[] {
  const out: SightmapComponent[] = []
  for (const c of components ?? []) {
    out.push(c)
    out.push(...flattenComponents(c.children))
  }
  return out
}

export interface SightmapStats {
  views: number
  components: number
  requests: number
  memory: number
  properties: number
}

export function statsFor(doc: SightmapDoc): SightmapStats {
  const all = [...flattenComponents(doc.components), ...doc.views.flatMap((v) => flattenComponents(v.components))]
  return {
    views: doc.views.length,
    components: all.length,
    requests: (doc.requests?.length ?? 0) + doc.views.reduce((n, v) => n + (v.requests?.length ?? 0), 0),
    // View-level notes count too — they are the same kind of knowledge, filed one level up.
    memory: all.reduce((n, c) => n + (c.memory?.length ?? 0), 0)
      + doc.views.reduce((n, v) => n + (v.memory?.length ?? 0), 0),
    properties: all.reduce((n, c) => n + (c.properties?.length ?? 0), 0),
  }
}
